import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { registerPush } from "@/lib/push";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);     // user object | false (not authed) | null (loading)
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            // Best-effort push registration after we know who the user is
            registerPush();
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { refresh(); }, [refresh]);

    const login = async (email, password) => {
        // CRITICAL: try to register push BEFORE auth call. The login button
        // click is a valid user gesture, which is required by Chrome/Safari
        // to call Notification.requestPermission(). Doing it here means we
        // get "100% automatic enrollment" the moment someone tries to log in.
        try { registerPush({ promptIfNeeded: true }); } catch (_) { /* best-effort */ }
        const { data } = await api.post("/auth/login", { email, password });
        if (data?.mfa_required) {
            return data;   // caller (Login.jsx) handles the second step UI
        }
        if (data?.step_up) {
            return data;   // caller handles trusted-device OTP challenge
        }
        setUser(data);
        return data;
    };

    const stepUpVerify = async (challenge_id, otp, trust_device) => {
        const { data } = await api.post("/auth/step-up", { challenge_id, otp, trust_device });
        setUser(data);
        try { registerPush({ promptIfNeeded: true }); } catch (_) { /* best-effort */ }
        return data;
    };

    const mfaVerify = async (method, code) => {
        const { data } = await api.post("/auth/mfa/verify", { method, code });
        setUser(data);
        try { registerPush({ promptIfNeeded: true }); } catch (_) { /* best-effort */ }
        return data;
    };

    const mfaSendEmail = async () => {
        const { data } = await api.post("/auth/mfa/email/send");
        return data;
    };

    const register = async (payload) => {
        const { data } = await api.post("/auth/register", payload);
        setUser(data);
        return data;
    };

    const otpRequest = async (identifier, channel = "sms") => {
        const { data } = await api.post("/auth/otp/request", { identifier, channel });
        return data;
    };

    const otpVerify = async (identifier, code) => {
        const { data } = await api.post("/auth/otp/verify", { identifier, code });
        setUser(data);
        try { registerPush({ promptIfNeeded: true }); } catch (_) { /* best-effort */ }
        return data;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch (_e) { /* ignore network errors on logout */ }
        setUser(false);
    };

    return (
        <AuthCtx.Provider value={{ user, loading, login, register, logout, otpRequest, otpVerify, refresh, mfaVerify, mfaSendEmail, stepUpVerify, apiError }}>
            {children}
        </AuthCtx.Provider>
    );
}

export const useAuth = () => useContext(AuthCtx);
